import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, IconButton, MenuItem, Paper, Select, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VillaIcon from '@mui/icons-material/Villa';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { X } from "lucide-react";

const childArr = Array.from({ length: 12 }, (_, i) => ({
    name: `${i + 1} year old`,
    value: `${i + 1}`,
}));

const GuestSearch = ({
    guetsValue,
    setGuestValue,
    holidayData,
    childAge,
    setChildAge,
    additionalCounters,
    setAdditionalCounters,
    customBorder = "searchMenu-guests"
}) => {
    const [errorMessage, setErrorMessage] = useState("");
    const [count, setCount] = useState(holidayData?.roomCount !== undefined ? holidayData?.roomCount : 1);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentEditingRoom, setCurrentEditingRoom] = useState(0); // Initially set to the first room
    const [expanded, setExpanded] = useState(additionalCounters.length === 1 ? 0 : null);


    useEffect(() => {
        if (guetsValue && guetsValue.Children > 0) {
            const myTempArr = [];
            for (let j = 0; j < guetsValue.Children; j++) {
                myTempArr.push(childAge[j] ? childAge[j] : { name: "1 year old", value: "1" });
            }
            setChildAge(myTempArr);
        } else {
            setChildAge([]);
        }
    }, [guetsValue]);

    useEffect(() => {
        // Initialize state for the single default room
        if (holidayData?.rooms) {
            const defaultRoom = {
                adults: holidayData.rooms[0]?.minPersonCount || 1,
                children: 0,
                childrenAges: [],
                Rooms: 1,
            };
            setAdditionalCounters([defaultRoom]);
        }
    }, [holidayData]);

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const maxRoom = holidayData?.roomCount;

    const handleRoomCountChange = (value) => {
        if (value > maxRoom) {
            setErrorMessage(`You've reached the limit of ${maxRoom} rooms!`);
            return;
        }
        if (value < 1) {
            setErrorMessage(`The minimum number of rooms is 1.`);
            return;
        }
        setErrorMessage("");

        const updatedCounters = [...additionalCounters];

        // Adding new rooms
        if (value > additionalCounters.length) {
            const newRooms = Array.from({ length: value - additionalCounters.length }, (_, i) => ({
                adults: holidayData?.rooms[i]?.minPersonCount || 1,
                children: 0,
                childrenAges: [],
                Rooms: value,
            }));
            setAdditionalCounters((prevCounters) => [...prevCounters, ...newRooms]);
        } else if (value < additionalCounters.length) {
            // Removing rooms
            setAdditionalCounters(updatedCounters.slice(0, value));
        }

        setCount(value); // Update the room count
        setGuestValue((prevValue) => ({
            ...prevValue,
            Rooms: value,
            Adults: updatedCounters.reduce((total, counter) => total + counter.adults, 0),
            Children: updatedCounters.reduce((total, counter) => total + counter.children, 0),
        }));
    };


    const handleAdultCountChange = (value, index) => {
        const maxPersons = holidayData?.rooms[index]?.adultCount;
        const minPersons = holidayData?.rooms[index]?.minPersonCount;
        if (value > maxPersons) {
            setErrorMessage(`The maximum adult count of ${maxPersons} for this room has been exceeded. Please add guests to another room.`);
            return;
        }
        if (value < minPersons) {
            setErrorMessage(`Each room must have at least ${minPersons} adult${minPersons > 1 ? 's' : ''}.`);
            return;
        }
        setErrorMessage("");

        const adjustedValue = value < minPersons ? minPersons : value;

        setAdditionalCounters((prevCounters) => {
            const updatedCounters = [...prevCounters];
            updatedCounters[index].adults = adjustedValue;
            const totalAdults = updatedCounters.reduce((acc, counter) => acc + counter.adults, 0);
            setGuestValue((prevValue) => ({
                ...prevValue,
                Adults: totalAdults,
            }));
            return updatedCounters;
        });
    };

    const handleChildCountChange = (value, index, childIndex) => {

        const maxPersons = holidayData.rooms[index]?.childCount;
        const minPersons = 0; // Assuming there's no minimum child count based on provided JSON
        if (value > maxPersons) {
            setErrorMessage(`The maximum child count of ${maxPersons} for this room has been exceeded. Please add guests to another room.`);
            return;
        }
        if (value < minPersons) {
            return; // No need to set error message for minimum child count
        }
        setErrorMessage("");
        setAdditionalCounters((prevCounters) => {
            const updatedCounters = [...prevCounters];
            updatedCounters[index].children = value;
            // If children were added, append default age
            if (value > updatedCounters[index].childrenAges.length) {
                const newAges = Array.from({ length: value - updatedCounters[index].childrenAges.length }, () => ({
                    name: "1 year old", value: "1"
                }));
                updatedCounters[index].childrenAges = [...updatedCounters[index].childrenAges, ...newAges];
            }
            // If children were removed, remove corresponding ages
            else if (value < updatedCounters[index].childrenAges.length) {
                updatedCounters[index].childrenAges = updatedCounters[index].childrenAges.slice(0, value);
            }

            const totalChildren = updatedCounters.reduce((acc, counter) => acc + counter.children, 0);
            setGuestValue((prevValue) => ({
                ...prevValue,
                Children: totalChildren,
            }));
            return updatedCounters;
        });
    };

    const handleChildAgesChange = (value, roomIndex, childIndex) => {
        const updatedCounters = additionalCounters.map((counter, index) => {
            if (index === roomIndex) {
                const updatedChildrenAges = [...counter.childrenAges];
                updatedChildrenAges[childIndex] = { name: childArr.find(opt => opt.value === value)?.name || "", value };

                return {
                    ...counter,
                    childrenAges: updatedChildrenAges,
                };
            }
            return counter;
        });

        // Calculate total children count
        const totalChildren = updatedCounters.reduce((acc, counter) => acc + counter.childrenAges.length, 0);

        setGuestValue((prevValue) => ({
            ...prevValue,
            ChildrenAges: totalChildren,
        }));

        setAdditionalCounters(updatedCounters);
    };


    function filterChildArrForRoom(roomIndex) {
        const room = holidayData?.rooms[roomIndex];
        if (room) {
            const [minAge, maxAge] = room.childAge.split("-").map(Number);
            return childArr.filter(option => {
                const age = parseInt(option.value);
                return age >= minAge && age <= maxAge;
            });
        }
        return [];
    }

    const handleEditRoom = (roomIndex) => {
        setCurrentEditingRoom(roomIndex);
    };

    const handleDeleteRoom = (roomIndex) => {
        if (additionalCounters.length > 1) {
            const updatedCounters = additionalCounters.filter((_, index) => index !== roomIndex);
            setAdditionalCounters(updatedCounters);
            setCount(updatedCounters.length); // Update the count
            setGuestValue((prevValue) => ({
                ...prevValue,
                Rooms: updatedCounters.length,
                Adults: updatedCounters.reduce((total, counter) => total + counter.adults, 0),
                Children: updatedCounters.reduce((total, counter) => total + counter.children, 0),
            }));
            setCurrentEditingRoom(updatedCounters.length - 1); // Adjust current editing room
        }
    };


    const handleAccordionChange = (index) => {
        setExpanded(expanded === index ? null : index);
    };

    return (
        <div className={`${customBorder} d-flex items-center js-form-dd js-form-counters justify-content-between position-relative`} style={{
            padding: '0px 12px'
        }}>
            <div
                data-bs-toggle="dropdowns"
                data-bs-auto-close="outside"
                aria-expanded="false"
                data-bs-offset="0,22"
            // onClick={handleOpenModal}
            >
                <p className="text-12 fw-500 ls-2 lh-16">Guests and Rooms</p>
                <div className="text-15 text-light-1 ls-2 lh-16">
                    <span className="js-count-adult">
                        {additionalCounters.reduce((acc, counter) => acc + counter.adults, 0)}
                    </span>{" "}
                    adults -{" "}
                    <span className="js-count-child">
                        {additionalCounters.reduce((acc, counter) => acc + counter.children, 0)}
                    </span>{" "}
                    children - <span className="js-count-room">{count}</span> rooms
                </div>
            </div>
            <div>
                <h6
                    className='staycation-change bg-light-4 text-light-1'
                    style={{
                        // backgroundColor: 'black',
                        padding: '5px 5px',
                        borderRadius: '6px',

                        cursor: 'pointer'
                    }}
                    onClick={handleOpenModal}
                >
                    {/* Select Date */}

                    <VillaIcon />
                </h6>
            </div>

            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="guests-and-rooms-modal"
                aria-describedby="modal-with-guests-and-rooms-selection"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div className="modal-content stay-holi-modal">
                    <div className="modal-body">
                        <div className="bg-white p-3 rounded-4 counter-box">
                            <div className="d-flex flex-column gap-4">
                                <div className="guest-selector" style={{
                                    display: "flex",
                                    justifyContent: "space-between"
                                }}>
                                    <Typography style={{
                                        textAlign: "start",
                                        fontSize: "24px",
                                        fontWeight: "600"
                                    }}>Select Your Rooms</Typography>
                                    <div display="flex" justifyContent="end" sx={{ position: "absolute", top: "0px", right: "0px" }}>
                                        <Button onClick={handleCloseModal} sx={{ color: 'black' }}>
                                            <X size={26} />
                                        </Button>
                                    </div>
                                </div>
                                {errorMessage && (
                                    <div style={{ color: "red" }}>{errorMessage}</div>
                                )}
                                <div className="d-flex justify-content-between align-items-center" style={{
                                    background: "#6dc8bf1f",
                                    padding: "10px", borderRadius: '5px'
                                }}>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Typography fontWeight={600}>Add Room</Typography>

                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#fff", padding: "5px 10px", borderRadius: "8px" }}>
                                            <IconButton size="small" style={{ border: "1px solid #45baae", borderRadius: "5px", cursor: "pointer" }} onClick={() => handleRoomCountChange(count - 1)}>
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>

                                            <Typography>{count}</Typography>

                                            <IconButton size="small" onClick={() => handleRoomCountChange(count + 1)} style={{ backgroundColor: "#44b9ae", color: "#fff", borderRadius: "5px", cursor: "pointer" }}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <Button variant="contained" style={{ borderRadius: "8px", backgroundColor: "#6dc8bf" }} onClick={handleCloseModal}>
                                        Submit
                                    </Button>

                                </div>
                                <div
                                    style={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        padding: "10px 0px",
                                        display: "grid",
                                        gap: "10px",
                                        /* Hide Scrollbar */
                                        scrollbarWidth: "none",  /* Firefox */
                                        msOverflowStyle: "none", /* Internet Explorer/Edge */
                                    }}
                                >
                                    {
                                        additionalCounters?.map((counter, roomIndex) => (
                                            <div key={roomIndex} className={`d-flex gap-1 ${expanded === roomIndex ? "align-items-start" : "align-items-center"}`}>
                                                <Accordion
                                                    key={roomIndex}
                                                    expanded={expanded === roomIndex}
                                                    onChange={() => handleAccordionChange(roomIndex)}
                                                    style={{ width: "100%", borderRadius: "8px", border: "1px solid #7575753b" }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        aria-controls={`room-3-content`}
                                                        id={`room-3-header`}
                                                        style={{ display: "flex", alignItems: "center" }}
                                                    >
                                                        {/* Left Side - Room Icon and Details */}
                                                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                                            {/* Bed Icon with Lamp */}
                                                            <img
                                                                src="/icon/rooms.svg"
                                                                alt="bed"
                                                                style={{ width: "40px", height: "40px" }}
                                                            />

                                                            {/* Room Details */}
                                                            <div className="text-start">
                                                                <Typography fontWeight={600}>{`Room ${roomIndex + 1}`}</Typography>
                                                                <Typography variant="body2" color="gray">
                                                                    {`Adult-${counter.adults}`} | {`Children-${counter.children}`}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </AccordionSummary>

                                                    <AccordionDetails style={{
                                                        background: "#f6f6f696"
                                                    }}>
                                                        <div className="d-flex justify-content-between">
                                                            {/* Left Side - Label */}
                                                            <div className="text-start">
                                                                <Typography fontWeight={600}>Adult</Typography>
                                                                <Typography variant="body2" color="gray">
                                                                    Age 12+
                                                                </Typography>
                                                            </div>

                                                            {/* Counter Section */}
                                                            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f9f9f9", padding: "5px 10px", borderRadius: "8px" }}>
                                                                <IconButton size="small" style={{ border: "1px solid #45baae", borderRadius: "5px", cursor: "pointer" }} onClick={() => handleAdultCountChange(counter.adults - 1, roomIndex)}
                                                                >
                                                                    <RemoveIcon fontSize="small" />
                                                                </IconButton>

                                                                <Typography>{counter?.adults}</Typography>

                                                                <IconButton size="small" style={{ backgroundColor: "#44b9ae", color: "#fff", borderRadius: "5px", cursor: "pointer" }} onClick={() => handleAdultCountChange(counter.adults + 1, roomIndex)}>
                                                                    <AddIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    </AccordionDetails>
                                                    <AccordionDetails style={{
                                                        background: "#f6f6f696",
                                                        borderTop: "0.2px solid #a5a1a196"
                                                    }}>
                                                        <div className="d-flex justify-content-between">
                                                            {/* Left Side - Label */}
                                                            <div className="text-start">
                                                                <Typography fontWeight={600}>Children</Typography>
                                                                <Typography variant="body2" color="gray">
                                                                    Age 0-12
                                                                </Typography>
                                                            </div>

                                                            {/* Counter Section */}
                                                            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f9f9f9", padding: "5px 10px", borderRadius: "8px" }}>
                                                                <IconButton size="small" style={{ border: "1px solid #45baae", borderRadius: "5px", cursor: "pointer" }} onClick={() => handleChildCountChange(counter.children - 1, roomIndex)} >
                                                                    <RemoveIcon fontSize="small" />
                                                                </IconButton>

                                                                <Typography>{counter?.children}</Typography>

                                                                <IconButton size="small" style={{ backgroundColor: "#44b9ae", color: "#fff", borderRadius: "5px", cursor: "pointer" }} onClick={() => handleChildCountChange(counter.children + 1, roomIndex)}>
                                                                    <AddIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                        <Box mt={2} className="d-flex flex-wrap gap-4">
                                                            {Array.from({ length: counter.children }, (_, childIndex) => (
                                                                // <div key={childIndex} className="d-flex gap-3 align-items-center">
                                                                //     <label htmlFor={`autocomplete-${roomIndex}-${childIndex}`}>Age {childIndex + 1}</label>
                                                                //     <Autocomplete
                                                                //         disablePortal
                                                                //         id={`autocomplete-${roomIndex}-${childIndex}`}
                                                                //         options={filterChildArrForRoom(roomIndex)}
                                                                //         onChange={(e, newValue) => handleChildAgesChange(newValue, roomIndex, childIndex)}
                                                                //         value={counter.childrenAges[childIndex] || { name: "1 year old", value: "1" }}
                                                                //         getOptionLabel={(option) => `${option.name}`}
                                                                //         style={{ width: 130 }}
                                                                //         renderInput={(params) => <TextField {...params} />}
                                                                //         size="small"
                                                                //     />
                                                                // </div>

                                                                <div key={childIndex} className="d-flex gap-3 align-items-center">
                                                                    <label htmlFor={`autocomplete-${roomIndex}-${childIndex}`}>Age {childIndex + 1}</label>
                                                                    <Select
                                                                        id={`autocomplete-${roomIndex}-${childIndex}`}
                                                                        value={counter.childrenAges[childIndex]?.value || "1"}
                                                                        onChange={(e) => handleChildAgesChange(e.target.value, roomIndex, childIndex)}
                                                                        displayEmpty
                                                                        style={{ width: 130 }}
                                                                    >
                                                                        {filterChildArrForRoom(roomIndex)?.map((option) => (
                                                                            <MenuItem key={option.value} value={option.value}>
                                                                                {option.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </div>
                                                            ))}
                                                        </Box>
                                                    </AccordionDetails>

                                                </Accordion>
                                                {
                                                    additionalCounters?.length > 1 &&
                                                    <div>
                                                        <IconButton
                                                            onClick={() => handleDeleteRoom(roomIndex)}
                                                            size="small"
                                                            sx={{
                                                                "&:hover": {
                                                                    color: "red",
                                                                    background: "#f2a3a36e" // Changes color on hover
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>

                                                    </div>
                                                }
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default GuestSearch;
